<template>
  <div :id="containerId" style="width: 100%; min-height: 400px;">
  </div>
</template>
<script>
import Highcharts from 'highcharts';
import 'highcharts/modules/data';
import 'highcharts/modules/exporting';
import 'highcharts/modules/offline-exporting';
import 'highcharts/modules/drilldown';
import 'highcharts/modules/no-data-to-display';
import { currencyMix } from '../../../shared/currency_mix';

export default {
  name: 'highchart-wrapper',
  mixins: [currencyMix],
  props: {
    containerId: {
      type: String,
      required: true,
    },
    payload: {
      type: Object,
      required: true,
    },
    useExporting: {
      type: Boolean,
      default: true,
    },
    isCurrency: {
      type: Boolean,
      default: false,
    }
  },
  computed: {
    // Set global things here, like credits and theme
    chartPayload() {
      // Turn off watermark
      let payload = this.payload;
      payload.credits = {
        enabled: false
      };

      payload.exporting = {
        enabled: this.useExporting,
        buttons: {
          contextButton: {
            symbol: 'menuball',
            symbolStrokeWidth: 1,
            symbolFill: '#27AA83',
            symbolStroke: '#324D67'
          }
        }
      };

      return payload;
    }
  },
  mounted() {
    // Set the theme
    Highcharts.theme = this.theme;
    Highcharts.setOptions(Highcharts.theme);
    // Experimental allowed tags in svg/html. not sure if needed anymore
    Highcharts.AST.allowedTags.push('div');
    Highcharts.AST.allowedTags.push('label');
    Highcharts.AST.allowedTags.push('i');
    Highcharts.AST.allowedTags.push('p');
    Highcharts.AST.allowedAttributes.push('class');
    // Save the original subtitle for resetting it on responsive mobile mouse out.
    let vm = this;
    this.initialSubtitle = this.chartPayload.subtitle.text;
    if (this.payload.chart.type === 'line') {
      // Thread in responsive subtitle changing for mobile on line charts
      var responsive = {
        rules: [{
          condition: {
            callback: function (e) {
              return window.matchMedia('screen and (max-width: 480px)').matches;
            }
          },
          chartOptions: {
            tooltip: {
              shadow: false,
              backgroundColor: 'rgba(0,0,0,0)',
              useHTML: true,
              formatter: function (e) {
                let subtitle = `<div class='flex gap-x-sm align-items-baseline justify-space-between m-b-md'>
                                <div class='text-blue-600 text-16 font-semi flex flex-col'>
                                    <div>${vm.isCurrency? vm.formatAsCurrency(this.points[0].point.y * 100) : this.points[0].point.y}</div>
                                    <div class='text-12'>${this.points[0].point.name}</div>
                                </div>
                                <div class='text-gray-600 text-14 font-normal flex flex-col text-right'>
                                    <div>${vm.isCurrency? vm.formatAsCurrency(this.points[1].point.y * 100) : this.points[1].point.y}</div>
                                    <div class='text-12'>${this.points[1].point.name}</div>
                                </div>
                              </div>`;
                // Use the subtitles on mobile to show the numbers so your finger is not in the way of the tooltip
                e.chart.subtitle.attr({
                  text: subtitle
                });
                // Force tooltip to disappear on mobile, thumbs get in the way
                return "<div style='display:none;'></div>";
              }
            }
          }
        }]
      };
      this.chartPayload.responsive = responsive;
    }

    if (this.payload.chart.type === 'bar') {
          // Thread in responsive subtitle changing for mobile on line charts
      var responsive = {
        rules: [{
          condition: {
            callback: function (e) {
              return window.matchMedia('screen and (max-width: 480px)').matches;
            }
          },
          chartOptions: {
            chart: {
              marginLeft: 100,
              marginRight: 10,
              spacingBottom: 10,
              scrollablePlotArea: {
                  minWidth: 600,
                  scrollPositionX: 0
              }
            },
            plotOptions: {
              bar: {
                minPointLength: 3,
                groupPadding: 1,
                pointWidth: 12
              }
            },
            drilldown: {
            breadcrumbs: {
              position: {
                align: 'left',
                verticalAlign: 'top'
              },
              floating: false // Try setting to false for mobile
            }
          }
          }
        }]
      };
      this.chartPayload.responsive = responsive;

      // Add a click handler for the drilldown and drill up events to redraw the chart
      this.chartPayload.chart.events = {
        ...(this.chartPayload.chart.events || {}),
        drilldown: function(e) {
          // Calculate height based on number of categories with minimum height
          const categories = e.seriesOptions.data.length;
          const newHeight = Math.max(400, categories * 40); // 40px per category, minimum 400px
          this.setSize(null, newHeight);
          this.redraw();
        },
        drillup: function(e) {
          // Calculate height based on number of categories in parent series with minimum height
          const categories = e.seriesOptions.data.length;
          const newHeight = Math.max(400, categories * 40); // 40px per category, minimum 400px
          this.setSize(null, newHeight);
          this.redraw();
        }
      };
    }

    if (this.payload.chart.type === 'pie' && this.payload.drilldown && this.payload.drilldown.series) {
      // Add legend configuration
      this.payload.legend = this.payload.legend || { events: { itemClick: null } };
      this.payload.legend.events.itemClick = function(event) {
          // Find the point that corresponds to this legend item
          const point = event.legendItem;
          if (point && point.drilldown) {
            // Find the series in our data
            const series = this.chart.series;
            // Find the point that matches this legend item name in our data
            let targetPoint = null;
            series.forEach(function(s) {
              s.points.forEach(function(p) {
                if (p.name === point.name) {
                  targetPoint = p;
                }
              });
            });

            if (targetPoint) {
               // Find the matching drilldown series data from the original options
                const drilldownSeries = this.chart.options.drilldown.series.find(
                  series => series.id === targetPoint.drilldown
                );
                
                if (drilldownSeries) {
                  // Trigger drilldown with the full series data
                  this.chart.addSeriesAsDrilldown(targetPoint, drilldownSeries);
                  return false; // Prevent default legend click
                }
            }
          }
          
        return true; // Allow default behavior if no drilldown exists
      }
    }

    Highcharts.chart(this.containerId, this.chartPayload);
  },
  data() {
    return {
      initialSubtitle: '',
      theme: {
        colors: [
          '#4299D7', // b4 - Blue
          '#66D6AD', // p3 - Green
          '#F7D06E', // y4 - Yellow
          '#8562C6', // pu3 - Purple
          '#D64343', // r4 - Red
          '#E8368F', // Custom pink
          '#F9703E', // Custom orange
          '#27AA83', // p5 - Green (darker)
          '#54D1DB'  // Custom teal
        ],
        chart: {
          backgroundColor: '#ffffff',
          style: {
            fontFamily: '"Open Sans", "Helvetica Neue", Helvetica, Arial, sans-serif'
          },
          spacing: [30, 10, 30, 10] // top, right, bottom, left
        },
        title: {
          style: {
            color: '#243A52', // n8 - Dark blue-gray
            font: 'bold 18px "Open Sans", "Helvetica Neue", Helvetica, Arial, sans-serif',
            marginBottom: '15px'
          }
        },
        subtitle: {
          style: {
            color: '#48647F', // n6 - Medium blue-gray
            font: 'normal 14px "Open Sans", "Helvetica Neue", Helvetica, Arial, sans-serif'
          }
        },
        legend: {
          itemStyle: {
            font: '12px "Open Sans", "Helvetica Neue", Helvetica, Arial, sans-serif',
            color: '#48647F', // n6 - Medium blue-gray
            fontWeight: 'normal'
          },
          itemHoverStyle: {
            color: '#243A52' // n8 - Dark blue-gray
          }
        },
        xAxis: {
          labels: {
            style: {
              color: '#243A52', // n8 - Dark blue-gray
              font: '12px "Open Sans", "Helvetica Neue", Helvetica, Arial, sans-serif',
              fontWeight: 'normal'
            }
          },
          lineColor: '#DAE2EC', // n1 - Light gray
          gridLineColor: '#DAE2EC' // n1 - Light gray
        },
        yAxis: {
          labels: {
            style: {
              color: '#243A52', // n8 - Dark blue-gray
              font: '12px "Open Sans", "Helvetica Neue", Helvetica, Arial, sans-serif',
              fontWeight: 'normal'
            }
          },
          lineColor: '#DAE2EC', // n1 - Light gray
          gridLineColor: '#DAE2EC' // n1 - Light gray
        },
        plotOptions: {
          bar: {
            borderRadius: 4,
            dataLabels: {
              style: {
                color: '#243A52', // n8 - Dark blue-gray
                textOutline: 'none',
                fontWeight: 'normal',
                fontSize: '13px'
              }
            }
          },
          series: {
            states: {
              hover: {
                brightness: 0.1
              }
            }
          }
        },
        tooltip: {
          backgroundColor: 'rgba(255, 255, 255, 0.95)',
          borderWidth: 1,
          borderColor: '#DAE2EC', // n1 - Light gray
          borderRadius: 8,
          shadow: true,
          style: {
            color: '#243A52', // n8 - Dark blue-gray
            fontSize: '14px',
            padding: '12px'
          }
        }
      }
    };
  }
};
</script>
